import { useMutation, useQuery } from "@apollo/client";
import {
  CONFIG_DEFAULTS,
  OnlineBookingMode,
  ServiceSelectionMode,
  WorkerSelectionMode,
} from "@salon/types";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { gql } from "@/gql";
import { useConfigurationSubscription } from "@/hooks/use-configuration-subscription";
import { useSalonStore } from "@/lib/store/salon";
import { useAutoSaveField } from "../../../hooks/use-auto-save-field";
import {
  normalizeOnlineBookingMode,
  normalizeServiceSelectionMode,
  normalizeWorkerSelectionMode,
} from "../utils/online-booking-enums";

const GET_ONLINE_BOOKING_CONFIGS = gql(`
  query GetSalonConfigurations($salonId: ID!, $category: String) {
    getSalonConfigurations(salonId: $salonId, filters: { category: $category }) {
      configurations {
        key
        value
        dataType
        category
        createdAt
        updatedAt
      }
    }
  }
`);

const UPDATE_SALON_CONFIGURATIONS = gql(`
  mutation UpdateSalonConfigurations($salonId: ID!, $input: UpdateSalonConfigurationsInput!) {
    updateSalonConfigurations(salonId: $salonId, input: $input) {
      key
      value
      dataType
      category
      updatedAt
    }
  }
`);

export interface OnlineBookingConfig {
  enabled: boolean;
  slotsPerHour: number;
  leadMinutesFromNow: number;
  leadMinutesFromOpening: number;
  trailMinutesBeforeClosing: number;
  mode: OnlineBookingMode;
  advanceBookingDays: number;
  workerSelectionEnabled: boolean;
  serviceSelectionMode: ServiceSelectionMode;
  workerSelectionMode: WorkerSelectionMode | null;
  allowBookingPastClosing: boolean;
  showServiceDuration: boolean;
  bankTransferEnabled: boolean;
}

export function useOnlineBookingConfig() {
  const { currentSalon } = useSalonStore();
  const selectedSalonId = currentSalon?.id;
  const [saving, setSaving] = useState(false);
  const [mutationError, setMutationError] = useState<Error | null>(null);

  // Real-time updates so changes from Partner/another tab show up immediately.
  useConfigurationSubscription({
    salonId: selectedSalonId ?? "",
    categories: ["online_booking"],
    enabled: !!selectedSalonId,
  });

  const {
    data,
    loading: isLoading,
    error,
  } = useQuery(GET_ONLINE_BOOKING_CONFIGS, {
    variables: {
      salonId: selectedSalonId ?? "",
      category: "online_booking",
    },
    skip: !selectedSalonId,
  });

  const [updateConfigsMutation] = useMutation(UPDATE_SALON_CONFIGURATIONS);

  // Parse configurations into typed object - show defaults even if no configurations exist
  const configurations = data?.getSalonConfigurations?.configurations || [];
  // Fallbacks come from canonical CONFIG_DEFAULTS in @salon/types — do not
  // hardcode here.
  const defaultValues: OnlineBookingConfig = useMemo(
    () => ({
      enabled: parseConfigValue(
        configurations,
        "online_booking.enabled",
        "boolean",
        CONFIG_DEFAULTS["online_booking.enabled"]
      ),
      slotsPerHour: parseConfigValue(
        configurations,
        "online_booking.slots_per_hour",
        "int",
        CONFIG_DEFAULTS["online_booking.slots_per_hour"]
      ),
      leadMinutesFromNow: parseConfigValue(
        configurations,
        "online_booking.lead_minutes_from_now",
        "int",
        CONFIG_DEFAULTS["online_booking.lead_minutes_from_now"]
      ),
      leadMinutesFromOpening: parseConfigValue(
        configurations,
        "online_booking.lead_minutes_from_opening",
        "int",
        CONFIG_DEFAULTS["online_booking.lead_minutes_from_opening"]
      ),
      trailMinutesBeforeClosing: parseConfigValue(
        configurations,
        "online_booking.trail_minutes_before_closing",
        "int",
        CONFIG_DEFAULTS["online_booking.trail_minutes_before_closing"]
      ),
      mode: parseConfigValue(
        configurations,
        "online_booking.mode",
        "online_booking_mode",
        CONFIG_DEFAULTS["online_booking.mode"]
      ),
      advanceBookingDays: parseConfigValue(
        configurations,
        "online_booking.advance_booking_days",
        "int",
        CONFIG_DEFAULTS["online_booking.advance_booking_days"]
      ),
      workerSelectionEnabled: parseConfigValue(
        configurations,
        "online_booking.worker_selection_enabled",
        "boolean",
        CONFIG_DEFAULTS["online_booking.worker_selection_enabled"]
      ),
      serviceSelectionMode: parseConfigValue(
        configurations,
        "online_booking.service_selection_mode",
        "service_selection_mode",
        CONFIG_DEFAULTS["online_booking.service_selection_mode"]
      ),
      workerSelectionMode: parseConfigValue(
        configurations,
        "online_booking.worker_selection_mode",
        "worker_selection_mode",
        CONFIG_DEFAULTS["online_booking.worker_selection_mode"]
      ),
      allowBookingPastClosing: parseConfigValue(
        configurations,
        "online_booking.allow_booking_past_closing",
        "boolean",
        CONFIG_DEFAULTS["online_booking.allow_booking_past_closing"]
      ),
      showServiceDuration: parseConfigValue(
        configurations,
        "online_booking.show_service_duration",
        "boolean",
        CONFIG_DEFAULTS["online_booking.show_service_duration"]
      ),
      bankTransferEnabled: parseConfigValue(
        configurations,
        "online_booking.bank_transfer_enabled",
        "boolean",
        CONFIG_DEFAULTS["online_booking.bank_transfer_enabled"]
      ),
    }),
    [configurations]
  );

  // Auto-sync with Apollo cache (initial load + subscription updates) while
  // preserving user edits in flight.
  const form = useForm<OnlineBookingConfig>({
    values: defaultValues,
    resetOptions: { keepDirtyValues: true },
    mode: "onChange",
  });

  const persistField = useCallback(
    async <K extends keyof OnlineBookingConfig>(name: K, value: OnlineBookingConfig[K]) => {
      if (!selectedSalonId) {
        throw new Error("No salon selected");
      }
      const dataType = getDataTypeForKey(name);
      return updateConfigsMutation({
        variables: {
          salonId: selectedSalonId,
          input: {
            configurations: [
              {
                key: `online_booking.${camelToSnakeCase(name)}`,
                value: serializeValue(value, dataType),
                dataType,
              },
            ],
          },
        },
        refetchQueries: [GET_ONLINE_BOOKING_CONFIGS],
      });
    },
    [selectedSalonId, updateConfigsMutation]
  );
  const { saveField, savingFields } = useAutoSaveField(form, persistField);

  // Save function
  const save = useCallback(async () => {
    if (!form.formState.isDirty || !selectedSalonId) {
      toast.info("No changes to save");
      return;
    }

    setSaving(true);
    setMutationError(null);

    try {
      const formData = form.getValues();

      // Build configurations array for bulk update
      const configurations = Object.keys(formData)
        .filter((key) => {
          const value = formData[key as keyof OnlineBookingConfig];
          // Skip null/undefined values (like workerSelectionMode when not set)
          return value !== null && value !== undefined;
        })
        .map((key) => {
          const configKey = `online_booking.${camelToSnakeCase(key)}`;
          const dataType = getDataTypeForKey(key as keyof OnlineBookingConfig);
          const stringValue = serializeValue(formData[key as keyof OnlineBookingConfig], dataType);

          return {
            key: configKey,
            value: stringValue,
            dataType,
          };
        });

      // Single bulk update mutation
      await updateConfigsMutation({
        variables: {
          salonId: selectedSalonId,
          input: { configurations },
        },
        refetchQueries: [GET_ONLINE_BOOKING_CONFIGS],
      });

      // Mark form as pristine after successful save
      // Don't reset values - let user continue interacting normally
      form.reset(form.getValues(), { keepValues: true });
      toast.success("Online booking configuration saved successfully");
    } catch (error) {
      console.error("Online booking configuration save error:", error);
      const mutationErr = error instanceof Error ? error : new Error("Save failed");
      setMutationError(mutationErr);
      toast.error(`Save failed: ${mutationErr.message}`);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [form, selectedSalonId, updateConfigsMutation]);

  // Discard function
  const discard = useCallback(() => {
    form.reset(defaultValues);
    setMutationError(null);
    toast.info("Changes discarded");
  }, [form, defaultValues]);

  const combinedError = error || mutationError;

  return {
    form,
    config: form.watch(), // Return current form values
    isLoading,
    saving,
    error: combinedError,
    save,
    discard,
    saveField,
    savingFields,
  };
}

// Helper functions
function parseConfigValue<T>(
  configurations: any[],
  key: string,
  dataType: string,
  defaultValue: T
): T {
  const config = configurations.find((c) => c.key === key);
  if (!config) return defaultValue;

  switch (dataType) {
    case "boolean":
      return (config.value.toLowerCase() === "true") as T;
    case "int":
      return Number.parseInt(config.value, 10) as T;
    case "online_booking_mode":
      return normalizeOnlineBookingMode(config.value) as T;
    case "service_selection_mode":
      return normalizeServiceSelectionMode(config.value) as T;
    case "worker_selection_mode":
      return normalizeWorkerSelectionMode(config.value) as T;
    case "outcome_tracking_mode":
      return config.value as T;
    default:
      return config.value as T;
  }
}

function camelToSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function getDataTypeForKey(key: keyof OnlineBookingConfig): string {
  const typeMap: Record<keyof OnlineBookingConfig, string> = {
    enabled: "boolean",
    slotsPerHour: "int",
    leadMinutesFromNow: "int",
    leadMinutesFromOpening: "int",
    trailMinutesBeforeClosing: "int",
    mode: "online_booking_mode",
    advanceBookingDays: "int",
    workerSelectionEnabled: "boolean",
    serviceSelectionMode: "service_selection_mode",
    workerSelectionMode: "worker_selection_mode",
    allowBookingPastClosing: "boolean",
    showServiceDuration: "boolean",
    bankTransferEnabled: "boolean",
  };
  return typeMap[key];
}

function serializeValue(value: any, dataType: string): string {
  switch (dataType) {
    case "boolean":
      return value ? "true" : "false";
    case "int":
      return value.toString();
    default:
      return value;
  }
}
